import React from "react";
import { TOOLS, SHAPES } from "../constants";
import { getCloudPath } from "../utils";

const CurrentShapeRenderer = ({
  isDrawing,
  currentTool,
  currentShape,
  startPoint,
  currentPath,
  color,
  strokeWidth,
  currentUser,
}) => {
  if (!isDrawing || currentTool !== TOOLS.SHAPE || !startPoint) return null;
  
  const endPoint = currentPath && currentPath.end ? currentPath.end : startPoint;
  
  const getUserName = () => {
    if (currentUser) {
      const name = `${currentUser.first_name || ""} ${currentUser.last_name || ""}`.trim();
      const displayName = name || currentUser.email || currentUser.username || "Unknown";
      return `Added by: ${displayName}`;
    }
    return "Added by: Unknown";
  };

  switch (currentShape) {
    case SHAPES.LINE:
      return (
        <g>
          <title>{getUserName()}</title>
          <line
            x1={startPoint.x}
            y1={startPoint.y}
            x2={endPoint.x}
            y2={endPoint.y}
            stroke={color}
            strokeWidth={strokeWidth}
          />
        </g>
      );
    case SHAPES.RECTANGLE:
      return (
        <g>
          <title>{getUserName()}</title>
          <rect
            x={Math.min(startPoint.x, endPoint.x)}
            y={Math.min(startPoint.y, endPoint.y)}
            width={Math.abs(endPoint.x - startPoint.x)}
            height={Math.abs(endPoint.y - startPoint.y)}
            stroke={color}
            strokeWidth={strokeWidth}
            fill="none"
          />
        </g>
      );
    case SHAPES.CIRCLE: {
      const left = Math.min(startPoint.x, endPoint.x);
      const top = Math.min(startPoint.y, endPoint.y);
      const width = Math.abs(endPoint.x - startPoint.x);
      const height = Math.abs(endPoint.y - startPoint.y);
      return (
        <g>
          <title>{getUserName()}</title>
          <ellipse
            cx={left + width / 2}
            cy={top + height / 2}
            rx={width / 2}
            ry={height / 2}
            stroke={color}
            strokeWidth={strokeWidth}
            fill="none"
          />
        </g>
      );
    }
    case SHAPES.ARROW: {
      const arrowLength = Math.sqrt(
        Math.pow(endPoint.x - startPoint.x, 2) +
          Math.pow(endPoint.y - startPoint.y, 2)
      );
      const angle = Math.atan2(
        endPoint.y - startPoint.y,
        endPoint.x - startPoint.x
      );
      const headlen = 15;
      const arrowX = endPoint.x - headlen * Math.cos(angle - Math.PI / 6);
      const arrowY = endPoint.y - headlen * Math.sin(angle - Math.PI / 6);
      const arrowX2 = endPoint.x - headlen * Math.cos(angle + Math.PI / 6);
      const arrowY2 = endPoint.y - headlen * Math.sin(angle + Math.PI / 6);
      return (
        <g>
          <title>{getUserName()}</title>
          <line
            x1={startPoint.x}
            y1={startPoint.y}
            x2={endPoint.x}
            y2={endPoint.y}
            stroke={color}
            strokeWidth={strokeWidth}
          />
          <polygon
            points={`${endPoint.x},${endPoint.y} ${arrowX},${arrowY} ${arrowX2},${arrowY2}`}
            fill={color}
          />
        </g>
      );
    }
    case SHAPES.POLYGON: {
      const midX = (startPoint.x + endPoint.x) / 2;
      return (
        <g>
          <title>{getUserName()}</title>
          <polygon
            points={`${startPoint.x},${endPoint.y} ${midX},${startPoint.y} ${endPoint.x},${endPoint.y}`}
            stroke={color}
            strokeWidth={strokeWidth}
            fill="none"
          />
        </g>
      );
    }
    case SHAPES.CLOUD: {
      const [x1, y1, x2, y2] =
        startPoint.x < endPoint.x
          ? [startPoint.x, startPoint.y, endPoint.x, endPoint.y]
          : [endPoint.x, endPoint.y, startPoint.x, startPoint.y];
      const cloudD = getCloudPath(x1, y1, x2, y2, 5, 8);
      return (
        <g>
          <title>{getUserName()}</title>
          <path
            d={cloudD}
            stroke={color}
            strokeWidth={strokeWidth}
            fill="none"
          />
        </g>
      );
    }
    default:
      return null;
  }
};

export default CurrentShapeRenderer;

